import React, {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';

import { Box } from '../Box';
import { Button } from '../Button';
import { Icon } from '../Icon';
import * as styles from './Modal.styles';
import type { ModalProps } from './Modal.types';

/**
 * Modal component for displaying content in an overlay dialog.
 * Supports keyboard navigation, focus trapping, and accessibility features.
 */
export const Modal = ({
  id,
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnOutsideClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  closeButtonIcon = 'Xmark',
  scrollLayerStyles: customScrollLayerStyles,
  containerStyles: customContainerStyles,
  contentStyles: customContentStyles,
  closeButtonStyles: customCloseButtonStyles,
  className,
  ariaLabel,
  ariaDescribedBy,
}: ModalProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousScrollY = useRef<number>(0);
  const previousActiveElement = useRef<Element | null>(null);

  // Handle escape key press
  const handleKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape' && onClose) {
        onClose(event as unknown as KeyboardEvent);
      }
    },
    [closeOnEscape, onClose]
  );

  // Prevent background scrolling and manage focus when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position and active element
      previousScrollY.current = window.scrollY;
      previousActiveElement.current = document.activeElement;

      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${previousScrollY.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);

      // Focus the modal container for accessibility
      if (containerRef.current) {
        containerRef.current.focus();
      }
    } else {
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, previousScrollY.current);

      // Restore focus to previous element
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Cleanup body styles on unmount
      document.body.style.position = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleClose = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      if (onClose) {
        onClose(event);
      }
    },
    [onClose]
  );

  const handleBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (
        !closeOnOutsideClick ||
        !containerRef.current ||
        containerRef.current.contains(event.target as Node)
      ) {
        return;
      }
      handleClose(event);
    },
    [closeOnOutsideClick, handleClose]
  );

  if (!isOpen) {
    return null;
  }

  // Ensure we're in a browser environment for portal rendering
  if (typeof document === 'undefined') {
    return null;
  }

  const closeButton = showCloseButton && (
    <Button
      onClick={handleClose}
      css={[
        styles.closeButtonStyles,
        size === 'full' && styles.closeButtonFullStyles,
        customCloseButtonStyles,
      ]}
      data-testid={`modal-close-button-${id}`}
      aria-label="Close modal"
      variant="text"
      size="xs"
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        gap="var(--spacing-2)"
      >
        <Icon variant={closeButtonIcon} size="medium" />
        <span>Close</span>
      </Box>
    </Button>
  );

  return ReactDOM.createPortal(
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={0}
      right={0}
      bottom={0}
      left={0}
      width="100%"
      zIndex={9999}
      className={className}
      role="presentation"
      data-testid={`modal-${id}`}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex="1"
        overflow="auto"
        p="var(--spacing-4)"
        css={[styles.scrollLayerStyles, customScrollLayerStyles]}
        onClick={handleBackdropClick}
        onKeyDown={(e) => e.key === 'Escape' && handleClose(e)}
        role="presentation"
        data-testid={`modal-backdrop-${id}`}
      >
        <Box
          ref={containerRef}
          display="flex"
          flexDirection="column"
          position="relative"
          overflow="hidden"
          css={[
            styles.containerStyles,
            styles.sizeStyles[size],
            customContainerStyles,
          ]}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          tabIndex={-1}
        >
          {closeButton}
          <Box flex="1" overflow="auto" css={customContentStyles}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

Modal.displayName = 'Modal';

export default Modal;
